/**
 * Shared design system CSS for HTML iframes (streaming + static).
 *
 * Injected into both PersistentHtmlRenderer's IFRAME_SRCDOC and
 * StaticHtmlBlock's wrappedHtml so all LLM-generated HTML gets
 * consistent styling.
 *
 * KEY DESIGN: All elements inherit text color from their parent/body.
 * This enables AI-generated dark containers with explicit text colors
 * to cascade correctly to children. A JS contrast insurance script
 * catches remaining cases where AI sets backgrounds without text colors.
 *
 * Design tokens match the native React module components in modules/shared.jsx.
 */

export const IFRAME_DESIGN_CSS = `
/* ── Design Tokens ── */
:root {
  --text-xs: clamp(10px, 2.5vw, 11px);
  --text-sm: clamp(12px, 3vw, 13px);
  --text-base: clamp(14px, 3.5vw, 15px);
  --text-lg: clamp(16px, 4vw, 18px);
  --text-xl: clamp(18px, 4.5vw, 22px);
  --text-2xl: clamp(22px, 5.5vw, 28px);
  --glass-bg: rgba(128, 128, 128, 0.05);
  --glass-border: rgba(128, 128, 128, 0.12);
  --glass-hover: rgba(128, 128, 128, 0.08);
  --accent: #000;
  --accent-dim: rgba(128, 128, 128, 0.15);
  --accent-glow: rgba(128, 128, 128, 0.06);
  --card-bg: #fff;
  --card-radius: 20px;
}

/* ── Force light mode — prevent dark-mode Tailwind from activating ── */
html { color-scheme: light !important; }
@media (prefers-color-scheme: dark) {
  :root { color-scheme: light !important; }
}

/* ── Knockout-label safety (prod 3019cce0) ──
   The design token --vi-bg is transparent and must NEVER be a label/fill color; an
   inverted pill authored as background:var(--vi-text|--vi-accent);color:var(--vi-bg)
   renders its label invisible over a dark pill (a solid black box) on the app WebView.
   This iframe defines neither --vi-bg nor --vi-text so it degrades to readable inherited
   text today — the rule is a no-op here now, kept in lockstep with the app stylesheet
   (assets/html/vi_components.css) and the api-server persist-side rewrite so the contract
   is symmetric and future-proof if this iframe ever defines the tokens. */
[style*="color:var(--vi-bg)"],
[style*="color: var(--vi-bg)"] { color: var(--vi-accent-text) !important; }

/* ── Base Reset ── */
* { margin: 0; padding: 0; box-sizing: border-box; }

/* Standardized top-level wrapper (#2795). The model wraps its whole fragment in
   <div class="vi-root">; display:contents makes it layout-transparent so any
   padding/margin on it is a no-op and can never double-inset / lose width — the
   body's single horizontal inset is the only gutter. Parity with the app shell. */
#root > .vi-root { display: contents; }
/* ...and force the outermost content to FILL the container width, edge-to-edge of the
   freeform-html block. Models habitually style their top card with max-width:360px +
   margin:0 auto (a centered phone card), which leaves big side gutters on the
   already-mobile-width container. Neutralize that so the card fills #root. The body
   carries NO horizontal gutter (full-bleed, #2795): the freeform-html CARD CHROME (the
   message bubble) is the single outer inset, and the model card's own inner padding is
   the readable gutter — exactly like the native cards. No magic-number bleed. */
#root > .vi-root > div,
#root > .vi-root > section { max-width: none !important; width: auto !important; margin-left: 0 !important; margin-right: 0 !important; }

html, body {
  max-width: 100%;
  overflow-x: hidden;
}

/* ── Neutralize viewport-relative heights inside #root ──
   LLM-generated HTML often uses Tailwind h-screen / min-h-screen / *-dvh / *-svh
   or raw 100vh. Inside an iframe whose height tracks content, those classes
   create a feedback loop: iframe grows → vh grows → child grows → iframe grows.
   Force them to content-sized values. */
#root [class*="h-screen"],
#root [class*="h-dvh"],
#root [class*="h-svh"],
#root [class*="h-lvh"] {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
}
#root [style*="100vh"],
#root [style*="100dvh"],
#root [style*="100svh"],
#root [style*="100lvh"] {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
}

body {
  font-family: 'Inter', 'SF Pro Display', system-ui, -apple-system, sans-serif;
  background: #fff;
  color: rgba(0, 0, 0, 0.82);
  padding: env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px);
  overflow-x: hidden;
  font-size: var(--text-base);
  line-height: 1.75;
  letter-spacing: 0.01em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  touch-action: pan-y;
}

::-webkit-scrollbar { display: none; }
button, [data-action] { cursor: pointer; }
button:active, [data-action]:active { transform: scale(0.97); }
.hidden { display: none !important; }

/* ── Typography ── */
/* All elements inherit text color from parent/body for theme flexibility.
   AI-generated dark containers with explicit text colors cascade correctly
   to children. JS contrast insurance catches remaining mismatches.
   Differentiation is via font-weight, font-size, and spacing — not color. */
h1, h2, h3, h4, h5 {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.4em;
  margin-bottom: 0.5em;
}
h1:first-child, h2:first-child, h3:first-child, h4:first-child { margin-top: 0; }
h1 { font-size: var(--text-2xl); letter-spacing: -0.025em; }
h2 { font-size: var(--text-xl); letter-spacing: -0.015em; }
h3 { font-size: var(--text-lg); }
h4 { font-size: var(--text-base); }

p {
  margin-bottom: 0.75em;
}
p:last-child { margin-bottom: 0; }

strong { font-weight: 600; }

a {
  color: #007AFF;
  text-decoration: none;
  transition: color 0.2s;
}
a:hover { color: #0051d5; }

/* ── Lists ── */
ul, ol {
  padding-left: 1.4em;
  margin-bottom: 1em;
}
li {
  margin-bottom: 0.35em;
  line-height: 1.65;
}
li::marker { color: color-mix(in srgb, currentColor 35%, transparent); }

/* ── Blockquote ── */
blockquote {
  border-left: 3px solid color-mix(in srgb, currentColor 25%, transparent);
  padding: 0.6em 1em;
  margin: 1em 0;
  background: rgba(128, 128, 128, 0.06);
  border-radius: 0 var(--card-radius) var(--card-radius) 0;
  font-style: italic;
  opacity: 0.85;
}

/* ── Code ── */
code {
  padding: 0.15em 0.4em;
  border-radius: 6px;
  font-size: 0.88em;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  background: rgba(128, 128, 128, 0.1);
}
pre {
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 16px;
  padding: 1em;
  overflow-x: auto;
  margin: 1em 0;
  background: rgba(128, 128, 128, 0.06);
}
pre code { background: none; padding: 0; }

/* ── Tables ── */
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 1em 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(128, 128, 128, 0.15);
}
thead th {
  font-weight: 600;
  text-align: left;
  padding: 0.65em 0.9em;
  font-size: var(--text-sm);
  border-bottom: 1px solid rgba(128, 128, 128, 0.15);
  background: rgba(128, 128, 128, 0.06);
}
tbody td {
  padding: 0.55em 0.9em;
  border-bottom: 1px solid rgba(128, 128, 128, 0.04);
  font-size: var(--text-sm);
}
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover { background: rgba(128, 128, 128, 0.05); }

/* ── Images ── */
img {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid rgba(128, 128, 128, 0.12);
}

/* ── Horizontal Rule ── */
hr {
  border: none;
  height: 1px;
  background: rgba(128, 128, 128, 0.15);
  margin: 1.5em 0;
}

/* ── Details / Summary ── */
details {
  border: 1px solid rgba(128, 128, 128, 0.12);
  border-radius: 16px;
  padding: 0.75em 1em;
  margin: 0.6em 0;
}
summary {
  cursor: pointer;
  font-weight: 500;
}

/* ── Card Utility Classes ── */
.vi-card {
  background: var(--card-bg);
  border: 1px solid var(--glass-border);
  border-radius: var(--card-radius);
  padding: 1em;
  margin-bottom: 0.8em;
  color: rgba(0, 0, 0, 0.82);
}
.vi-section {
  background: rgba(128, 128, 128, 0.04);
  border-radius: 16px;
  padding: 0.75em;
}
.vi-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 100px;
  border: 1px solid var(--glass-border);
  background: var(--card-bg);
  font-size: var(--text-xs);
  color: rgba(0, 0, 0, 0.5);
}
.vi-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.55em 1.1em;
  border-radius: 14px;
  border: 1px solid var(--glass-border) !important;
  background: var(--card-bg) !important;
  color: rgba(0, 0, 0, 0.6) !important;
  font-weight: 500;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all 0.2s;
}
.vi-btn:hover { background: var(--glass-hover) !important; }
.vi-btn:active { transform: scale(0.97); }
.vi-btn-primary {
  background: #000 !important;
  border-color: #000 !important;
  color: #fff !important;
}
.vi-btn-primary:hover { background: rgba(0, 0, 0, 0.85) !important; }
.vi-accent-bar {
  height: 2px;
  background: rgba(128, 128, 128, 0.15);
  border-radius: 100px;
  margin: 0.6em 0;
}

/* ── Streaming Animation ── */
#root > *:last-child {
  animation: viSlideIn 0.3s ease-out;
}
@keyframes viSlideIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── Fire-and-continue media slot ──
   The model authors ONLY \`<div data-vi-media="pending">🖼️ Generating…</div>\`
   (kept minimal on purpose — no per-turn token cost); the HOST styles it into a
   media-shaped loading box with a spinner ring above the model's own text.
   media_fill replaces the whole div with the real <img>/<video>, so these styles
   vanish with the pending state. Mirrors app assets/html/vi_components.css. */
[data-vi-media="pending"] {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 180px;
  border-radius: 16px;
  background: var(--glass-bg);
  border: 1px dashed var(--glass-border);
  color: rgba(128, 128, 128, 0.9);
  font-size: var(--text-sm);
  font-weight: 500;
  animation: viPendingPulse 1.6s ease-in-out infinite;
}
[data-vi-media="pending"]::before {
  content: "";
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 2.5px solid rgba(128, 128, 128, 0.25);
  border-top-color: rgba(128, 128, 128, 0.85);
  animation: viPendingSpin 0.9s linear infinite;
}
@keyframes viPendingSpin { to { transform: rotate(360deg); } }
@keyframes viPendingPulse { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  [data-vi-media="pending"], [data-vi-media="pending"]::before { animation: none; }
}
`;

/**
 * Contrast insurance script — injected into iframe <script> tags.
 * Scans rendered HTML for low-contrast text (dark text on dark bg, or light on light)
 * and fixes it automatically. Uses WCAG luminance calculation.
 *
 * Call _viFixContrast() after HTML rendering is complete (with a small delay
 * to let Tailwind CDN process classes).
 */
export const CONTRAST_INSURANCE_JS = `
var _viContrastTimer = null;
function _viFixContrast() {
  function parseC(s) {
    if (!s || s === 'transparent' || s === 'rgba(0, 0, 0, 0)') return null;
    var m = s.match(/rgba?\\((\\d+),\\s*(\\d+),\\s*(\\d+)(?:,\\s*([\\d.]+))?\\)/);
    if (!m) return null;
    var a = m[4] !== undefined ? parseFloat(m[4]) : 1;
    if (a < 0.3) return null;
    return [+m[1], +m[2], +m[3]];
  }
  function lum(r, g, b) {
    var c = [r / 255, g / 255, b / 255].map(function(v) {
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }
  function effBg(el) {
    while (el && el !== document.documentElement) {
      var c = parseC(getComputedStyle(el).backgroundColor);
      if (c) return c;
      el = el.parentElement;
    }
    return [255, 255, 255];
  }
  var root = document.getElementById('root') || document.body;
  var els = root.querySelectorAll('*');
  for (var i = 0; i < els.length; i++) {
    var el = els[i];
    var hasText = false;
    for (var j = 0; j < el.childNodes.length; j++) {
      if (el.childNodes[j].nodeType === 3 && el.childNodes[j].textContent.trim()) {
        hasText = true; break;
      }
    }
    if (!hasText) continue;
    var bg = effBg(el);
    var tc = parseC(getComputedStyle(el).color);
    if (!tc) continue;
    var bgL = lum(bg[0], bg[1], bg[2]);
    var tcL = lum(tc[0], tc[1], tc[2]);
    var ratio = (Math.max(bgL, tcL) + 0.05) / (Math.min(bgL, tcL) + 0.05);
    if (ratio < 2.5) {
      if (el.tagName === 'A') {
        el.style.color = bgL < 0.4 ? '#93c5fd' : '#1d4ed8';
      } else {
        el.style.color = bgL < 0.4 ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)';
      }
    }
  }
}
function _viScheduleContrast() {
  clearTimeout(_viContrastTimer);
  _viContrastTimer = setTimeout(_viFixContrast, 250);
}
`;
